import { useCallback, useState } from 'react';

import { Granularity } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { DateRangeStateResult, DateRangeType } from '../types';

/**
 * Options for the date range state hook.
 * Date range values come from URL (single source of truth).
 */
export interface UseDateRangeStateOptions {
  /** Current start time from URL */
  startTime: Date;
  /** Current end time from URL */
  endTime: Date;
  /** Current date range type from URL */
  dateRangeType: DateRangeType;
  /** Current granularity from URL */
  granularity: Granularity;
  /** Callback to update date range in URL */
  setDateRange: (startTime: Date, endTime: Date, granularity: Granularity, dateRangeType: DateRangeType) => void;
}

/**
 * Custom hook to manage date range callout UI state.
 *
 * The date range values (startTime, endTime, granularity, dateRangeType) come from
 * the URL - this hook only manages the callout visibility and provides callbacks
 * that update the URL when the user applies changes.
 *
 * @param options - Date range values and setter from URL state
 * @returns Object containing date range state and handlers
 */
export function useDateRangeState(options: UseDateRangeStateOptions): DateRangeStateResult {
  // Only callout UI state is managed locally - date range values come from URL (options)
  const [isDateRangeCalloutVisible, setIsDateRangeCalloutVisible] = useState(false);
  const [dateRangeButtonElement, setDateRangeButtonElement] = useState<HTMLElement | null>(null);

  const onToggleDateRangeCallout = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setDateRangeButtonElement(event.currentTarget as HTMLElement);
    setIsDateRangeCalloutVisible((prev) => !prev);
  }, []);

  const onApplyDateRange = useCallback(
    (newStartTime: Date, newEndTime: Date, newGranularity: Granularity, newDateRangeType: DateRangeType) => {
      // Update URL (single source of truth)
      options.setDateRange(newStartTime, newEndTime, newGranularity, newDateRangeType);
      setIsDateRangeCalloutVisible(false);
    },
    [options.setDateRange]
  );

  const onCancelDateRange = useCallback(() => {
    setIsDateRangeCalloutVisible(false);
  }, []);

  return {
    // Date range values from URL (single source of truth)
    startTime: options.startTime,
    endTime: options.endTime,
    granularity: options.granularity,
    dateRangeType: options.dateRangeType,
    // Callout UI state (managed locally)
    calloutState: {
      isVisible: isDateRangeCalloutVisible,
      targetElement: dateRangeButtonElement,
    },
    onToggleDateRangeCallout,
    onApplyDateRange,
    onCancelDateRange,
  };
}
